import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Briefcase, Loader2, MessageCircle, Upload, X, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/site/Seo";
import { SectionTag } from "@/components/site/Reveal";
import ParticleField from "@/components/site/ParticleField";
import { fetchJobs, submitJobApplication } from "@/lib/api";
import { useContactInfo } from "@/context/SiteContentContext";

const APPLY_LABELS = {
  ar: {
    apply_title: "قدّم طلبك",
    full_name: "الاسم الكامل",
    email: "البريد الإلكتروني",
    phone: "رقم الهاتف",
    linkedin: "LinkedIn (اختياري)",
    cover_letter: "لماذا تريد الانضمام؟",
    cv_label: "السيرة الذاتية (PDF)",
    cv_hint: "PDF فقط • بحد أقصى 10MB",
    submit: "إرسال الطلب",
    submitting: "جارٍ الإرسال...",
    success_title: "تم استلام طلبك",
    success_msg: "شكرًا لك! سنراجع سيرتك الذاتية ونتواصل معك قريبًا.",
    close: "إغلاق",
    apply_now: "قدّم الطلب الآن",
    whatsapp: "تواصل واتساب",
    required: "حقل مطلوب",
    invalid_email: "بريد إلكتروني غير صالح",
    file_required: "الرجاء رفع ملف PDF",
  },
  en: {
    apply_title: "Submit your application",
    full_name: "Full name",
    email: "Email",
    phone: "Phone",
    linkedin: "LinkedIn (optional)",
    cover_letter: "Why do you want to join?",
    cv_label: "CV / Resume (PDF)",
    cv_hint: "PDF only • Max 10MB",
    submit: "Submit application",
    submitting: "Submitting...",
    success_title: "Application received",
    success_msg: "Thank you! We'll review your CV and get back to you soon.",
    close: "Close",
    apply_now: "Apply now",
    whatsapp: "WhatsApp",
    required: "Required",
    invalid_email: "Invalid email",
    file_required: "Please upload a PDF",
  },
  ku: {
    apply_title: "داواکاریت بنێرە",
    full_name: "ناوی تەواو",
    email: "ئیمەیل",
    phone: "ژمارەی مۆبایل",
    linkedin: "LinkedIn (ئیختیاری)",
    cover_letter: "بۆچی دەتەوێت بەشدار بی؟",
    cv_label: "سی ڤی (PDF)",
    cv_hint: "تەنها PDF • زۆرترین ١٠MB",
    submit: "ناردنی داواکاری",
    submitting: "لە ناردندایە...",
    success_title: "داواکارییەکەت وەرگیرا",
    success_msg: "سوپاس! سی ڤیەکەت دەخوێنینەوە و بەم زووانە پەیوەندیت پێوە دەکەین.",
    close: "داخستن",
    apply_now: "داواکاری",
    whatsapp: "واتساب",
    required: "پێویستە",
    invalid_email: "ئیمەیل هەڵەیە",
    file_required: "تکایە PDF بار بکە",
  },
};

function ApplyModal({ job, onClose, lang }) {
  const L = APPLY_LABELS[lang] || APPLY_LABELS.ar;
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", linkedin: "", cover_letter: "" });
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = L.required;
    if (!form.email.trim()) e.email = L.required;
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = L.invalid_email;
    if (!cv) e.cv = L.file_required;
    if (cv && cv.type !== "application/pdf") e.cv = "PDF only";
    if (cv && cv.size > 10 * 1024 * 1024) e.cv = "Max 10MB";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate() || busy) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("full_name", form.full_name);
    fd.append("email", form.email);
    fd.append("phone", form.phone);
    fd.append("linkedin", form.linkedin);
    fd.append("cover_letter", form.cover_letter);
    fd.append("position", job?.title || "");
    fd.append("job_id", job?.id || "");
    fd.append("lang", lang);
    if (cv) fd.append("cv", cv);
    try {
      await submitJobApplication(fd);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full rounded-xl bg-[#0b1731] border border-white/10 focus:border-[color:var(--primary)] px-4 py-2.5 text-sm outline-none";

  return (
    <motion.div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="apply-modal"
    >
      <motion.div
        className="relative w-full max-w-lg rounded-3xl card-surface p-6 sm:p-8 my-8"
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 end-4 h-8 w-8 grid place-items-center rounded-full border border-white/10 hover:border-[color:var(--primary)] text-slate-400" aria-label="Close" data-testid="apply-close">
          <X size={16} />
        </button>

        {done ? (
          <div className="text-center py-6" data-testid="apply-success">
            <CheckCircle2 size={64} className="text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-extrabold mb-2">{L.success_title}</h3>
            <p className="text-slate-400 mb-6">{L.success_msg}</p>
            <button onClick={onClose} className="rounded-full px-6 py-2.5 font-bold text-[#04120a]" style={{ background: "var(--primary)" }}>
              {L.close}
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3" data-testid="apply-form">
            <div>
              <h3 className="text-2xl font-extrabold mb-1">{L.apply_title}</h3>
              {job && <div className="text-sm text-primary font-semibold mb-3">{job.title}</div>}
            </div>
            <div>
              <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder={L.full_name + " *"} className={inputCls} data-testid="apply-name" />
              {errors.full_name && <div className="text-xs text-red-400 mt-1">{errors.full_name}</div>}
            </div>
            <div>
              <input type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={L.email + " *"} className={inputCls} data-testid="apply-email" />
              {errors.email && <div className="text-xs text-red-400 mt-1">{errors.email}</div>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={L.phone} className={inputCls} data-testid="apply-phone" />
              <input dir="ltr" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder={L.linkedin} className={inputCls} data-testid="apply-linkedin" />
            </div>
            <textarea rows={3} value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} placeholder={L.cover_letter} className={inputCls} data-testid="apply-cover" />
            <div>
              <label className="block cursor-pointer" data-testid="apply-cv-label">
                <div className={"rounded-xl border-2 border-dashed border-white/15 hover:border-[color:var(--primary)] px-4 py-4 flex items-center gap-3 " + (cv ? "bg-[color:var(--primary)]/5" : "bg-[#0b1731]/60")}>
                  {cv ? <FileText size={22} className="text-primary shrink-0" /> : <Upload size={22} className="text-slate-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold truncate">{cv ? cv.name : L.cv_label + " *"}</div>
                    <div className="text-xs text-slate-500">{cv ? `${(cv.size / 1024 / 1024).toFixed(2)} MB` : L.cv_hint}</div>
                  </div>
                </div>
                <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setCv(e.target.files?.[0] || null)} data-testid="apply-cv-input" />
              </label>
              {errors.cv && <div className="text-xs text-red-400 mt-1">{errors.cv}</div>}
            </div>
            <button type="submit" disabled={busy} className="w-full rounded-full py-3 font-extrabold text-[#04120a] disabled:opacity-60 flex items-center justify-center gap-2" style={{ background: "var(--primary)" }} data-testid="apply-submit">
              {busy ? <><Loader2 size={16} className="animate-spin" /> {L.submitting}</> : L.submit}
            </button>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function Careers() {
  const { t, lang } = useLang();
  const CONTACT_INFO = useContactInfo();
  const [jobs, setJobs] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [openApply, setOpenApply] = useState(false);
  const L = APPLY_LABELS[lang] || APPLY_LABELS.ar;

  useEffect(() => { fetchJobs(lang).then((r) => setJobs(r.data)).catch(() => setJobs([])); }, [lang]);

  const startApply = (j) => { setActiveJob(j); setOpenApply(true); };

  return (
    <SiteLayout>
      <Seo title={t.careers.title} description={t.careers.subtitle} />
      <section className="relative overflow-hidden pt-16 pb-10" data-testid="careers-page">
        <ParticleField className="opacity-40" density={30} />
        <div className="absolute inset-0 radial-fade" />
        <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8">
          <SectionTag>{t.careers.tag}</SectionTag>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-5 mb-4">{t.careers.title}</h1>
          <p className="text-slate-400 text-lg max-w-2xl">{t.careers.subtitle}</p>
          <div className="mt-6">
            <button
              onClick={() => startApply(null)}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-[#04120a]"
              style={{ background: "var(--primary)" }}
              data-testid="general-apply-btn"
            >
              <Upload size={16} /> {L.apply_now}
            </button>
          </div>
        </div>
      </section>

      <section className="section-pad pt-6">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          {jobs === null ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
          ) : jobs.length === 0 ? (
            <p className="text-center text-slate-500 py-20">{t.careers.empty}</p>
          ) : (
            <div className="space-y-5">
              {jobs.map((j, i) => (
                <motion.div key={j.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="card-surface rounded-3xl p-7" dir="auto" data-testid={`job-${i}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[220px]">
                      <h3 className="text-2xl font-extrabold mb-3">{j.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-400 mb-4">
                        <span className="flex items-center gap-1.5"><Briefcase size={15} className="text-primary" /> {j.type}</span>
                        <span className="flex items-center gap-1.5"><MapPin size={15} className="text-primary" /> {j.location}</span>
                      </div>
                      {j.description && <p className="text-slate-300 leading-relaxed mb-3">{j.description}</p>}
                      {j.requirements && (
                        <div>
                          <div className="text-sm font-bold text-primary mb-1">{t.careers.requirements}:</div>
                          <p className="text-slate-400 leading-relaxed text-sm">{j.requirements}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        onClick={() => startApply(j)}
                        className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-[#04120a]"
                        style={{ background: "var(--primary)" }}
                        data-testid={`job-apply-${i}`}
                      >
                        <Upload size={16} /> {L.apply_now}
                      </button>
                      <a href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent("مرحبًا، أرغب بالتقديم على وظيفة: " + j.title)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs font-bold border border-white/15 hover:border-[color:var(--primary)]" data-testid={`job-whatsapp-${i}`}>
                        <MessageCircle size={14} /> {L.whatsapp}
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {openApply && <ApplyModal job={activeJob} lang={lang} onClose={() => setOpenApply(false)} />}
      </AnimatePresence>
    </SiteLayout>
  );
}
